import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService, permissionService } from '../../services';
import type { Role, Permission } from '../../services';
import { logError } from '../utils/error-handler';

// Define Membership type locally since it's no longer exported from services
interface Membership {
  id: string;
  userId: string;
  organizationId?: string;
  teamId?: string;
  roleId: string;
  role?: Role;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  updatedAt: string;
}
import { PermissionString, PermissionChecker } from '../utils/permissions';

// Context interface
interface RBACContextType {
  roles: Role[];
  permissions: Permission[];
  userPermissions: Permission[];
  aggregatedPermissions: PermissionString[]; // NEW: Aggregated permission strings
  currentUserMemberships: Membership[]; // NEW: Current user's memberships
  permissionChecker?: PermissionChecker; // NEW: Permission checker instance
  isLoading: boolean;
  error: string | null;
  loadRoles: (params?: { organizationId?: string; teamId?: string; level?: 'system' | 'organization' | 'team'; includePermissions?: boolean }) => Promise<Role[]>;
  loadPermissions: () => Promise<Permission[]>;
  loadUserPermissions: (scope?: { organizationId?: string; teamId?: string }) => Promise<Permission[]>;
  loadUserMembershipsAndAggregatePermissions: (scope?: { organizationId?: string; teamId?: string }) => Promise<void>; // NEW
  refreshPermissions: () => Promise<void>; // NEW: Refresh all permission data
  createRole: (data: { name: string; description?: string; permissions: string[]; organizationId?: string; teamId?: string; level: 'system' | 'organization' | 'team' }) => Promise<Role>;
  updateRole: (id: string, data: { name?: string; description?: string; permissions?: string[] }) => Promise<Role>;
  deleteRole: (id: string) => Promise<void>;
  assignRoleToUser: (userId: string, roleId: string, scope: { organizationId?: string; teamId?: string }) => Promise<void>;
  removeRoleFromUser: (userId: string, roleId: string, scope: { organizationId?: string; teamId?: string }) => Promise<void>;
  hasPermission: (permission: string, resource: string, scope?: { organizationId?: string; teamId?: string }) => Promise<boolean>;
}

// Create context with default values
const RBACContext = createContext<RBACContextType>({
  roles: [],
  permissions: [],
  userPermissions: [],
  aggregatedPermissions: [],
  currentUserMemberships: [],
  permissionChecker: undefined,
  isLoading: false,
  error: null,
  loadRoles: async () => [],
  loadPermissions: async () => [],
  loadUserPermissions: async () => [],
  loadUserMembershipsAndAggregatePermissions: async () => {},
  refreshPermissions: async () => {},
  // Reverted default mock data to use original Role type
  createRole: async () => ({ 
    _id: '', 
    name: '', 
    permissions: [], // string[]
    level: 'organization' as const, // Use level
    isDefault: false, // Add isDefault
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }),
  updateRole: async () => ({ 
    _id: '', 
    name: '', 
    permissions: [], // string[]
    level: 'organization' as const, // Use level
    isDefault: false, // Add isDefault
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }),
  deleteRole: async () => {},
  assignRoleToUser: async () => {},
  removeRoleFromUser: async () => {},
  hasPermission: async () => false,
});

// Hook to use RBAC context
export const useRBAC = () => useContext(RBACContext);

// Provider component
export const RBACProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganizationId, currentTeamId } = useOrganization();
  
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [aggregatedPermissions, setAggregatedPermissions] = useState<PermissionString[]>([]);
  const [currentUserMemberships, setCurrentUserMemberships] = useState<Membership[]>([]);
  const [permissionChecker, setPermissionChecker] = useState<PermissionChecker>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cache for permission checks to avoid redundant API calls
  const [permissionCache, setPermissionCache] = useState<Record<string, boolean>>({});


  // Load roles based on scope (organization, team, etc.)
  const loadRoles = useCallback(async (params?: { 
    organizationId?: string; 
    teamId?: string; 
    level?: 'system' | 'organization' | 'team';
    includePermissions?: boolean
  }): Promise<Role[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to get roles
      const fetchedRoles = await roleService.getRoles(params);
      setRoles(fetchedRoles);
      return fetchedRoles;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load roles';
      setError(errorMessage);
      
      // Return empty array if API call fails
      console.log('Error loading roles from API:', errorMessage);
      
      // Set roles to empty array
      setRoles([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);


  // Load all available permissions
  const loadPermissions = useCallback(async (): Promise<Permission[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to get permissions
      const fetchedPermissions = await permissionService.getPermissions();
      setPermissions(fetchedPermissions as any);
      return fetchedPermissions as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load permissions';
      setError(errorMessage);
      
      // Return empty array if API call fails
      console.log('Error loading permissions from API:', errorMessage);
      setPermissions([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load permissions for the current user
  const loadUserPermissions = useCallback(async (scope?: {
    organizationId?: string;
    teamId?: string
  }): Promise<Permission[]> => {
    if (!user) return [];

    setIsLoading(true);
    setError(null);

    try {
      // IMPORTANT: Do not pass scope when loading user permissions for general UI display
      // The context filtering should only be used for specific permission checks
      // Users should get ALL their permissions from ALL contexts (organization + team roles)
      // If scope is explicitly provided (not from currentOrganizationId/currentTeamId), use it
      const queryScope = scope;

      // Make the actual API call to get current user's permissions (API only supports /users/me/permissions)
      const result = await permissionService.getUserPermissions(undefined, queryScope);
      const permissionObjects = result.permissions || [];
      
      // Transform permission objects into Permission objects (they may be objects or strings)
      const userPerms: Permission[] = (permissionObjects as (Permission | string)[]).map((permission: Permission | string, index: number) => {
        if (typeof permission === 'string') {
          const [resource, action] = permission.split(':');
          return {
            id: `perm_${index}`,
            name: permission,
            resource: resource || '',
            action: action || '',
            description: `${resource}:${action} permission`
          } as unknown as Permission;
        } else {
          // Permission object already has the correct structure
          return {
            id: permission.id || `perm_${index}`,
            name: permission.name || `${permission.resource}:${permission.action}`,
            resource: permission.resource || '',
            action: permission.action || '',
            description: permission.description || `${permission.resource}:${permission.action} permission`
          } as unknown as Permission;
        }
      });
      
      setUserPermissions(userPerms);
      return userPerms;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load user permissions';
      setError(errorMessage);
      
      // Return empty array if API call fails
      console.log('Error loading user permissions from API:', errorMessage);
      
      // Set user permissions to empty array
      setUserPermissions([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // NEW: Load user permissions directly from API
  const loadUserMembershipsAndAggregatePermissions = useCallback(async (scope?: { 
    organizationId?: string; 
    teamId?: string 
  }): Promise<void> => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 1. Get user memberships for context tracking
      const { memberships } = await userService.getUserMemberships();
      
      // Transform UserMembership[] to Membership[] by adding missing properties
      const transformedMemberships: Membership[] = memberships.map(m => ({
        id: m.id,
        userId: m.userId,
        organizationId: m.organizationId,
        teamId: m.teamId,
        roleId: m.roleId,
        role: undefined, // Will be populated separately if needed
        status: 'active' as const, // Assume active since user has membership
        createdAt: m.joinedAt || new Date().toISOString(),
        updatedAt: m.joinedAt || new Date().toISOString()
      }));
      
      setCurrentUserMemberships(transformedMemberships);

      // 2. Load permissions for current context (org or team)
      // The backend automatically includes hierarchical permissions:
      // - When team is selected: system + organization + team permissions
      // - When org is selected: system + organization permissions
      // This ensures subscription/payment features (org-level) work even when a team is selected
      const contextScope = scope || (currentTeamId ? {
        organizationId: undefined,
        teamId: currentTeamId // Backend will include parent org permissions automatically
      } : currentOrganizationId ? {
        organizationId: currentOrganizationId,
        teamId: undefined // Org context only when no team selected
      } : undefined);

      // 3. Use loadUserPermissions with team context
      // Backend will return: system + org role + current team role only
      const userPerms = await loadUserPermissions(contextScope);

      // 4. Extract permission strings from the loaded permissions
      const permissionStrings = userPerms.map((perm) => {
        if (typeof perm === 'string') return perm;
        return perm.name || `${perm.resource}:${perm.action}`;
      });

      // 5. Update aggregated permissions state
      setAggregatedPermissions(permissionStrings);

      // 6. Create permission checker
      const checker = new PermissionChecker(permissionStrings);
      setPermissionChecker(checker);
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load user permissions';
      setError(errorMessage);
      console.error('🔒 Error loading user permissions:', errorMessage);
      
      // Reset permission state on error
      setAggregatedPermissions([]);
      setCurrentUserMemberships([]);
      setPermissionChecker(undefined);
      setUserPermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, currentOrganizationId, currentTeamId, loadUserPermissions]);

  // NEW: Refresh all permission data
  const refreshPermissions = useCallback(async (): Promise<void> => {
    if (!user || !isAuthenticated) return;
    
    try {
      // Reload memberships and aggregate permissions
      await loadUserMembershipsAndAggregatePermissions();
    } catch (error) {
      console.error('Failed to refresh permissions:', error);
    }
  }, [user, isAuthenticated, loadUserMembershipsAndAggregatePermissions]);

  // Create a new role
  const createRole = async (data: {
    name: string;
    description?: string;
    permissions: string[];
    organizationId?: string;
    teamId?: string;
    level: 'system' | 'organization' | 'team';
  }): Promise<Role> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to create a role
      const newRole = await roleService.createRole(data);
      
      // Update roles list
      setRoles(prevRoles => [...prevRoles, newRole]);
      
      return newRole;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create role';
      setError(errorMessage);
      
      logError('Error creating role', err);
      
      // Don't create a mock role in case of API error
      console.error('Failed to create role with API, not creating mock role');
      
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Update an existing role
  const updateRole = async (id: string, data: {
    name?: string;
    description?: string;
    permissions?: string[];
  }): Promise<Role> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to update the role
      const updatedRole = await roleService.updateRole(id, data);
      
      // Update roles list
      setRoles(prevRoles => 
        prevRoles.map(role => role._id === id ? updatedRole : role)
      );
      
      return updatedRole;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update role';
      setError(errorMessage);
      
      logError('Error updating role', err);
      
      // No fallback, just reject the promise
      console.error('Failed to update role with API, not creating local update');
      
      // Don't update the roles list with a fake update
      
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Delete a role
  const deleteRole = async (id: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to delete the role
      await roleService.deleteRole(id);
      
      // Update roles list
      setRoles(prevRoles => prevRoles.filter(role => role._id !== id));
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete role';
      setError(errorMessage);
      
      logError('Error deleting role', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Assign a role to a user
  const assignRoleToUser = async (
    userId: string, 
    roleId: string, 
    scope: { 
      organizationId?: string; 
      teamId?: string 
    }
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to assign role to user
      await roleService.assignRoleToUser(userId, roleId, scope);
      
      // If the role is assigned to the current user, refresh their permissions
      if (user && user._id === userId) {
        await loadUserPermissions();
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to assign role to user';
      setError(errorMessage);
      
      logError('Error assigning role to user', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a role from a user
  const removeRoleFromUser = async (
    userId: string, 
    roleId: string, 
    scope: { 
      organizationId?: string; 
      teamId?: string 
    }
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Make the actual API call to remove role from user
      await roleService.removeRoleFromUser(userId, roleId, scope);
      
      // If the role is removed from the current user, refresh their permissions
      if (user && user._id === userId) {
        await loadUserPermissions();
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to remove role from user';
      setError(errorMessage);
      
      logError('Error removing role from user', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  // Check if the current user has a specific permission
  const hasPermission = async (
    permission: string, 
    resource: string, 
    scope?: { 
      organizationId?: string; 
      teamId?: string 
    }
  ): Promise<boolean> => {
    if (!user) return false;
    
    // Use the provided scope or default to current context
    const queryScope = scope || {
      organizationId: currentOrganizationId || undefined,
      teamId: currentTeamId || undefined
    };
    
    // Create a cache key based on the permission, resource, and scope
    const cacheKey = `${permission}:${resource}:${queryScope.organizationId || ''}:${queryScope.teamId || ''}`;
    
    // Check if we have a cached result
    if (permissionCache[cacheKey] !== undefined) {
      return permissionCache[cacheKey];
    }
    
    try {
      // Make the actual API call to check permission
      const result = await permissionService.checkPermission({
        permissions: `${resource}:${permission}`,
        scope: queryScope
      });
      
      // Cache the result
      setPermissionCache(prev => ({
        ...prev,
        [cacheKey]: result
      }));
      
      return result;
    } catch (err) {
      console.error('Permission check failed:', err);
      
      // For demo purposes, grant permission if API call fails
      // In production, this should be more restrictive
      console.log(`API permission check failed for ${permission}:${resource}, defaulting to granted`);
      
      // Cache the result
      setPermissionCache(prev => ({
        ...prev,
        [cacheKey]: true
      }));
      
      return true;
    }
  };

  // Load initial data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Use a small delay to ensure token has been properly set
      // This helps prevent race conditions with OrganizationContext
      const loadContextData = async () => {
        console.log('RBACContext: User authenticated, preparing to load data', {
          userId: user._id,
          authenticated: isAuthenticated,
          hasToken: !!localStorage.getItem('accessToken'),
          orgId: currentOrganizationId,
          teamId: currentTeamId
        });
        
        try {
          // NEW: Load user memberships and aggregate permissions
          await new Promise(resolve => setTimeout(resolve, 200));
          await loadUserMembershipsAndAggregatePermissions();
          
          console.log('RBACContext: Successfully initialized with permission aggregation');
        } catch (error) {
          console.error('RBACContext: Error during initialization:', error);
        }
      };
      
      loadContextData();
    }
  }, [isAuthenticated, user, loadUserMembershipsAndAggregatePermissions]);

  // NEW: Handle context changes (organization/team switching)
  useEffect(() => {
    if (isAuthenticated && user && (currentOrganizationId || currentTeamId)) {
      console.log('RBACContext: Context changed, reloading permissions', {
        organizationId: currentOrganizationId,
        teamId: currentTeamId
      });
      
      // Reload permissions for new context with proper error handling
      const reloadPermissions = async () => {
        try {
          await loadUserMembershipsAndAggregatePermissions({
            organizationId: currentOrganizationId || undefined,
            teamId: currentTeamId || undefined
          });
        } catch (error) {
          console.error('Failed to reload permissions on context change:', error);
          // Don't throw the error further, just log it
        }
      };
      
      reloadPermissions();
    }
  }, [currentOrganizationId, currentTeamId, isAuthenticated, user, loadUserMembershipsAndAggregatePermissions]);

  // Clear data when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear all permission-related data for security
      setRoles([]);
      setUserPermissions([]);
      setAggregatedPermissions([]);
      setCurrentUserMemberships([]);
      setPermissionChecker(undefined);
      setPermissionCache({});
      console.log('RBACContext: Cleared all permission data on logout');
    }
  }, [isAuthenticated]);

  // Context value
  const value: RBACContextType = {
    roles,
    permissions,
    userPermissions,
    aggregatedPermissions,
    currentUserMemberships,
    permissionChecker,
    isLoading,
    error,
    loadRoles,
    loadPermissions,
    loadUserPermissions,
    loadUserMembershipsAndAggregatePermissions,
    refreshPermissions,
    createRole,
    updateRole,
    deleteRole,
    assignRoleToUser,
    removeRoleFromUser,
    hasPermission,
  };

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
};
